import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Sparkles, ArrowRight, Wand2, GitBranch, Eye, Loader2, AlertCircle } from "lucide-react";
import { api } from "../lib/api";
import BlueprintCard from "../components/BlueprintCard";
import GameBlueprintCard from "../components/GameBlueprintCard";
import { toast } from "sonner";

export default function Preview() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    api.get(`/preview/${id}`).then(({ data }) => {
      if (alive) { setData(data); setLoading(false); }
    }).catch((err) => {
      if (!alive) return;
      setError(err.response?.status === 404 ? "Preview not found or private." : "Could not load preview.");
      setLoading(false);
    });
    return () => { alive = false; };
  }, [id]);

  const remix = () => {
    const bp = data?.blueprint;
    if (!bp) return;
    if (data.kind === "game") {
      // Stash in sessionStorage so GameStudio picks it up as a draft
      try {
        sessionStorage.setItem("dpa_remix_game", JSON.stringify({
          ...bp,
          id: undefined, user_id: undefined, saved: false, is_public: false,
          remixed_from: bp.id,
        }));
      } catch { /* ignore */ }
      nav("/games?remix=1");
      toast.success("Remix loaded — keep iterating");
    } else {
      try {
        sessionStorage.setItem("dpa_remix_app", JSON.stringify({
          ...bp,
          id: undefined, user_id: undefined, saved: false, is_public: false,
          remixed_from: bp.id,
        }));
      } catch { /* ignore */ }
      nav("/studio?remix=1");
      toast.success("Remix loaded — keep iterating");
    }
  };

  if (loading) {
    return (
      <div className="container-narrow py-20 flex flex-col items-center gap-3 text-ink2" data-testid="preview-loading">
        <Loader2 className="w-6 h-6 animate-spin text-marigold" />
        <div className="text-sm">Loading preview…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container-narrow py-16 text-center" data-testid="preview-error">
        <AlertCircle className="w-10 h-10 mx-auto text-marigold mb-4" />
        <h1 className="font-heading text-3xl font-semibold mb-2">Preview unavailable</h1>
        <p className="text-ink2 mb-6">{error}</p>
        <Link to="/" className="btn-primary text-sm py-2 inline-flex">
          <Sparkles className="w-4 h-4" /> Back to Durga Programming Adventures
        </Link>
      </div>
    );
  }

  const { kind, blueprint } = data || {};
  const turns = blueprint?.refine_turns || [];

  return (
    <div className="container-narrow py-10 md:py-14" data-testid="preview-page">
      <header className="text-center mb-8">
        <div className="label-eyebrow mb-3 inline-flex items-center gap-2 justify-center">
          <Eye className="w-4 h-4" /> Public preview · {kind === "game" ? "Apple Game Blueprint" : "Apple App Blueprint"}
        </div>
        <h1 className="font-heading text-3xl md:text-5xl font-semibold leading-tight" data-testid="preview-title">
          {blueprint?.title}
        </h1>
        {blueprint?.author_name && (
          <p className="text-ink2 mt-2 text-sm">
            By <span className="font-semibold text-ink">{blueprint.author_name}</span>
            {blueprint?.shared_at && <> · shared {new Date(blueprint.shared_at).toLocaleDateString()}</>}
          </p>
        )}
        <div className="flex flex-wrap items-center justify-center gap-2 mt-5">
          <button onClick={remix} className="btn-primary text-sm py-2" data-testid="preview-remix-button">
            <Wand2 className="w-4 h-4" /> Remix in Studio
          </button>
          <button onClick={remix} className="btn-outline-ink text-sm py-2" data-testid="preview-branch-button">
            <GitBranch className="w-4 h-4" /> Branch this blueprint
          </button>
          <Link to={kind === "game" ? "/games" : "/studio"} className="text-sm inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-ink2 hover:text-ink transition">
            Build your own <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </header>

      {turns.length > 0 && (
        <section className="card-soft p-5 mb-6 bg-gradient-to-br from-cream via-paper to-saffron/5" data-testid="preview-history">
          <div className="text-xs uppercase tracking-wider text-marigold font-bold mb-3">
            How {blueprint?.author_name || "the author"} got here · {turns.length} refinement{turns.length === 1 ? "" : "s"}
          </div>
          <ol className="space-y-2 text-sm">
            {turns.map((t, i) => (
              <li key={i} className="flex items-start gap-2">
                <span className="w-6 h-6 rounded-full bg-ink text-cream text-xs font-mono flex items-center justify-center flex-shrink-0">
                  {i + 1}
                </span>
                <span className="text-ink2 leading-relaxed">{t}</span>
              </li>
            ))}
          </ol>
        </section>
      )}

      {kind === "game" ? (
        <GameBlueprintCard bp={blueprint} />
      ) : (
        <BlueprintCard bp={blueprint} />
      )}

      <div className="text-center mt-10 text-sm text-ink2">
        Built with <Link to="/" className="text-marigold font-semibold hover:underline">Durga Programming Adventures</Link> — natural-language Apple app & game blueprints.
      </div>
    </div>
  );
}
